import Checkbox from "@components/inputs/CheckBox";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { clsxm } from "../../../utils/clxm/clxm";
import { useClickOutside } from "../../../utils/hooks/useClickOutSide";
import { useForkRef } from "../../../utils/hooks/useForkRef";
import { MultiSelectLabel } from "./MultiSelectLabel";

export const MultiSelect = ({
  options,
  selectedValues = [],
  onChange,
  label,
  enableSearch,
  searchableLabels,
  color,
  baseColor,
  borderColor,
  expandedBorderColor,
  required = false,
}: {
  options: Option[];
  selectedValues: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  label?: string;
  enableSearch?: boolean;
  searchableLabels?: string[];
  color?: string;
  baseColor?: string;
  borderColor?: string;
  expandedBorderColor?: string;
  required?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [maxHeight, setMaxHeight] = useState("33vh");
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setMaxHeight(`${window.innerHeight / 3}px`);
    }
  }, []);

  const handleSelect = (value: string | number) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const fieldRef = useRef<HTMLElement | null>(null);

  const divRef = useClickOutside<HTMLDivElement>({
    handler: () => setExpanded(false),
  });
  const rootRef = useForkRef<HTMLDivElement>(divRef);

  const filteredOptions = useMemo(() => {
    if (searchQuery.trim() === "" || !searchableLabels) return options;
    return options.filter((option, index) =>
      searchableLabels[index].toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, options, searchableLabels]);

  return (
    <div className="flex flex-col w-full relative">
      {label && (
        <>
          <div className="flex items-center gap-2">
            <span className="text-text-base text-[10px] font-semibold">
              {label}
            </span>
            {required && (
              <span
                className={`text-[8px] font-semibold px-2 py-[2px] rounded-full transition-colors ${
                  selectedValues.length > 0
                    ? "bg-bg-status-green text-text-base"
                    : "bg-bg-status-red text-text-base"
                }`}
              >
                REQ
              </span>
            )}
          </div>
          <div className="my-[3px]" />
        </>
      )}
      <div
        role="button"
        ref={rootRef}
        className="relative flex flex-col w-full"
        onClick={() => setExpanded(!expanded)}
      >
        <MultiSelectLabel
          selectedValues={selectedValues}
          options={options}
          color={color}
          baseColor={baseColor}
          expanded={expanded}
          borderColor={borderColor}
          expandedBorderColor={expandedBorderColor}
        />

        {expanded && (
          <div
            className={clsxm(
              `absolute border-t-0 border-l border-r border-b border-${expandedBorderColor} left-0 rounded-[0px_0px_8px_8px] flex flex-col w-full z-50 shadow-lg`,
            )}
            style={{
              top: fieldRef.current?.clientHeight ?? "100%",
              borderTop: "1px solid var(--border-subtle)",
              backgroundColor: "var(--bg-page-body)",
              maxHeight: maxHeight,
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div ref={contentRef}>
              {enableSearch && (
                <div className="p-[10px]">
                  <GlobalTextInputField
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                  />
                </div>
              )}
              {filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  className="flex items-center justify-between px-3 h-12 cursor-pointer border-t first:border-t-0 border-border-subtle"
                  onClick={() => handleSelect(option.value)}
                >
                  <div className="flex items-center text-[12px] font-normal">
                    {option.label}
                  </div>
                  <Checkbox
                    checked={selectedValues.includes(option.value)}
                    onChange={() => handleSelect(option.value)}
                    isEnabled
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export type Option = {
  label: string | React.ReactElement;
  value: string | number;
};
